import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import { createFooterMenuSection, createFooterEndSection } from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "14px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 50px",
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Returns Header Section ======================
const createReturnsHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "93px 0",
    backgroundColor: "#F7F7F7",
    backdropFilter: "blur(14.3px)",
    color: "forground",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "60px 0",
    },
    mobile: {
      padding: "40px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1382px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
            mobile: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Returns & Refunds Policy",
              styles: {
                textAlign: "left",
                fontFamily: "Manrope, sans-serif",
                fontSize: "70px",
                fontStyle: "normal",
                fontWeight: "800",
                lineHeight: "80px",
                letterSpacing: "-2.1px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "60px",
                  lineHeight: "60px",
                },
                tablet: {
                  fontSize: "50px",
                  lineHeight: "50px",
                },
                mobile: {
                  fontSize: "45px",
                  lineHeight: "45px",
                  fontWeight: "700",
                },
              },
            },
            {
              id: generateId(),
              type: "heading" as const,
              content: "Date Effective: April 13, 2025",
              styles: {
                textAlign: "center",
                fontFamily: "Manrope, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "80px",
                letterSpacing: "-1.2px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                tablet: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  textAlign: "left",
                  fontSize: "25px",
                  lineHeight: "25px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Returns Content Section ======================
const createReturnsContentSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "120px 0 104px",
    backgroundColor: "background",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "80px 0",
    },
    mobile: {
      padding: "60px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        display: "flex",
        flexDirection: "column",
        gap: "50px",
        maxWidth: "1339px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        // Each policy section as a container with heading and paragraphs
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Returns & Refunds Policy",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "55px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.55px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "45px",
                  lineHeight: "45px",
                },
                mobile: {
                  fontSize: "40px",
                  lineHeight: "40px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `At Movyo, your satisfaction is our priority. We stand behind the quality of our products and want you to feel confident in trying them. If for any reason you're not fully satisfied with your purchase, we offer a simple and straightforward refund process. This policy explains how you can return your product and request a refund, as well as what to expect along the way.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `We believe in giving our customers time to try our product and determine if it's the right fit for them. That's why we offer a 60-day money-back guarantee from the date of your original purchase. Whether the product is unopened, opened, or even used, you may request a refund within that time frame by following the steps outlined below.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Eligibility for Refunds
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Eligibility for Refunds",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `To qualify for a refund, the product must have been purchased directly from Movyo.net. If your order was placed through a third-party retailer, this policy does not apply, and you will need to contact the original seller. Additionally, your refund request must be submitted within 60 days of your order date. Requests made after this period may not be eligible.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Products that have been damaged due to misuse, negligence, or improper storage are not eligible for refunds. Refunds are limited to one return per customer per product. Repeat refund requests from the same customer may be denied at our discretion.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: How to Request a Refund
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "How to Request a Refund",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `To initiate a return, please begin by contacting our refund department. We ask that all refund requests be submitted via email so we can properly track and process them. This also ensures that we can provide you with the most up-to-date return instructions and the correct return address.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Send an email to refund@movyo.net with the subject line "Refund Request – [Your Order Number]." In the body of the email, please include your full name, the email address used when placing the order, your order number (if available), and a brief description of why you are requesting a refund. While providing a reason is optional, it helps us improve our products and service.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Our team will respond to your request within one to two business days with a return authorization and mailing instructions. Please wait for confirmation before sending anything back to us.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Return Instructions
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Return Instructions",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `After you receive our response, you will be instructed to send the remaining product—whether sealed, opened, or even empty—back to our return center at the following address:`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: SAMPLE RETURNS DEPT
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "SAMPLE RETURNS DEPT",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Movyo\n19655 East 35th Drive, Suite #100\nAurora, CO. 80011\n+1 363 200 3599`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
                whiteSpace: "pre-line",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `We strongly recommend that you use a trackable shipping service when returning your package. Movyo is not responsible for items lost or damaged in transit. Return shipping costs are the responsibility of the customer and are not reimbursed.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Please ensure that your return includes your original packing slip or a note with your full name, email, and order number so we can match it to your account. Returns without identifying information may be delayed or denied.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Processing Your Refund
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Processing Your Refund",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `Once your returned product has been received and inspected at our facility, we will begin processing your refund. Refunds are issued to the original payment method used at checkout. Depending on your bank or credit card provider, it may take between five to seven business days for the refund to appear on your statement.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `We do not issue refunds for original shipping charges, if any were applied at checkout. In cases where a product was received damaged, please contact us immediately before initiating a return so we can evaluate whether a replacement or expedited resolution is more appropriate.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Additional Terms
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Additional Terms",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `This policy is designed to be fair to our customers while preventing abuse. We reserve the right to deny refunds or limit returns if we identify patterns of excessive or fraudulent return activity. We may also require proof of purchase and documentation in rare cases of disputes or chargebacks.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `By purchasing from Movyo.net, you agree to the terms of this Return & Refund Policy. This policy is subject to change at any time without notice. Updates will be posted to this page and the effective date will be revised accordingly.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Contact Us
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Contact Us",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `If you have any questions about this policy or need assistance with your return, our team is here to help. Please contact us directly at refund@movyo.net and include as much information as possible so we can assist you quickly and efficiently.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content: `We appreciate your trust in Movyo and thank you for giving our product a try.`,
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

export const template2Returns = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    returnsHeader: createReturnsHeaderSection(globalConfig) as BuilderElement,
    returnsContent: createReturnsContentSection(globalConfig) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2ReturnsGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
